import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  ProductAislesPreferenceMutationUpdateArgs,
  ProductAislesPreferenceUpdateFailure,
  ProductAislesPreferenceUpdateInput,
  UpdateProductAislesPreferenceMutation,
} from "@/graphql/codegen/graphql"
import { UpdateProductAislesPreferenceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateProductAislesPreferenceReturn {
  update: (input: ProductAislesPreferenceUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateProductAislesPreference = (onSuccess: () => void): UseUpdateProductAislesPreferenceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateProductAislesPreferenceMutation,
    ProductAislesPreferenceMutationUpdateArgs
  >(UpdateProductAislesPreferenceDocument)

  const update = async (input: ProductAislesPreferenceUpdateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.productAislesPreference.update
    const hasError = error || output?.__typename === "ProductAislesPreferenceUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message:
          error?.message ??
          (output as ProductAislesPreferenceUpdateFailure).error.message ??
          "Failed to update product aisles preference.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Product aisles preference successfully updated.",
    })
    onSuccess()
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
